import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { Calendar, Mail, CalendarDays, ArrowRight, Download, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';

export default function QuickLinksBar() {
  const { language } = useLanguage();
  const [calendarOpen, setCalendarOpen] = useState(false);

  return (
    <>
    <section data-testid="quick-links-bar" className="bg-white border-b border-[#E7EAEF] shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 divide-y md:divide-y-0 md:divide-x divide-[#E7EAEF]">
          <button
            onClick={() => setCalendarOpen(true)}
            data-testid="quick-link-calendar"
            className="flex items-center gap-4 px-6 py-4 hover:bg-[#E7EAEF]/50 transition-colors text-left w-full"
          >
            <div className="w-10 h-10 bg-[#F8CD46]/15 flex items-center justify-center flex-shrink-0">
              <Calendar className="w-5 h-5 text-[#F8CD46]" />
            </div>
            <div className="flex-1">
              <p className="text-[#14274B] font-semibold text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {language === 'fr' ? 'Calendrier 2026-2027' : 'Calendar 2026-2027'}
              </p>
              <p className="text-[#475569] text-xs" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {language === 'fr' ? 'Voir le calendrier scolaire' : 'View school calendar'}
              </p>
            </div>
            <ArrowRight className="w-4 h-4 text-[#475569]/40 flex-shrink-0" />
          </button>
          <Link
            to="/contact"
            data-testid="quick-link-contact"
            className="flex items-center gap-4 px-6 py-4 hover:bg-[#E7EAEF]/50 transition-colors"
          >
            <div className="w-10 h-10 bg-[#66CA9D]/15 flex items-center justify-center flex-shrink-0">
              <Mail className="w-5 h-5 text-[#66CA9D]" />
            </div>
            <div className="flex-1">
              <p className="text-[#14274B] font-semibold text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {language === 'fr' ? 'Contactez-nous' : 'Contact Us'}
              </p>
              <p className="text-[#475569] text-xs" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {language === 'fr' ? 'Questions ? Parlons-en' : 'Questions? Let\'s talk'}
              </p>
            </div>
            <ArrowRight className="w-4 h-4 text-[#475569]/40 flex-shrink-0" />
          </Link>
          <Link
            to="/events"
            data-testid="quick-link-events"
            className="flex items-center gap-4 px-6 py-4 hover:bg-[#E7EAEF]/50 transition-colors"
          >
            <div className="w-10 h-10 bg-[#2D78F6]/15 flex items-center justify-center flex-shrink-0">
              <CalendarDays className="w-5 h-5 text-[#2D78F6]" />
            </div>
            <div className="flex-1">
              <p className="text-[#14274B] font-semibold text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {language === 'fr' ? 'Événements' : 'Events'}
              </p>
              <p className="text-[#475569] text-xs" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {language === 'fr' ? 'Portes ouvertes virtuelles' : 'Virtual Open House'}
              </p>
            </div>
            <ArrowRight className="w-4 h-4 text-[#475569]/40 flex-shrink-0" />
          </Link>
        </div>
      </div>
    </section>

    {/* Calendar Modal */}
    <Dialog open={calendarOpen} onOpenChange={setCalendarOpen}>
      <DialogContent className="max-w-3xl w-[95vw] max-h-[90vh] p-0 overflow-hidden" data-testid="calendar-modal">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-[#E7EAEF]">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle
                className="text-xl font-semibold text-[#14274B]"
                style={{ fontFamily: 'Gabarito, sans-serif' }}
              >
                {language === 'fr' ? 'Calendrier Scolaire 2026-2027' : 'School Calendar 2026-2027'}
              </DialogTitle>
              <DialogDescription className="text-sm text-[#475569] mt-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {language === 'fr' ? 'Calendrier académique OFA Lycée France' : 'OFA Lycée France academic calendar'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <div className="overflow-y-auto px-6 py-4" style={{ maxHeight: 'calc(90vh - 160px)' }}>
          <img
            src="/calendar_2026_27.png"
            alt={language === 'fr' ? 'Calendrier scolaire 2026-2027' : 'School calendar 2026-2027'}
            className="w-full h-auto rounded"
            data-testid="calendar-image"
          />
        </div>
        <div className="px-6 py-4 border-t border-[#E7EAEF] bg-[#F8FAFC] flex items-center justify-between gap-3">
          <p className="text-xs text-[#475569]" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {language === 'fr' ? 'OFA Lycée France — Année scolaire 2026-2027' : 'OFA Lycée France — Academic year 2026-2027'}
          </p>
          <a
            href="/OFALycee_France_Calendar_2026-27.pdf"
            download
            data-testid="calendar-download-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#2D78F6] text-white text-sm font-medium hover:bg-[#14274B] transition-colors rounded"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            <Download className="w-4 h-4" />
            {language === 'fr' ? 'Télécharger (PDF)' : 'Download (PDF)'}
          </a>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}
